import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom-latest';
import Axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import useGenerateExternalUrl from 'hooks/useGenerateExternalUrl';
import { FIRM_ADMIN, SMART_VAULT_AVAILABILITY } from 'constants/constants';
import { getStorageData } from 'utilities/utils';
import SmartVaultLogo from 'images/smartvault-logo-mulberry.svg?react';

// SmartVault brand "mulberry" — matches the logo asset so the button and the
// modal accents read as the SmartVault brand affordance.
export const SMARTVAULT_MULBERRY = '#7B005F';

/**
 * Append `embed=1` to the SmartVault Mirror URL. `embed=1` tells the frontend to
 * render the page chrome-less (no left nav) — see apps/firm AppLayout. Built via
 * the URL API so an existing query/hash on the generated URL is preserved
 * correctly. Falls back to the raw URL if it can't be parsed (the hook always
 * returns a valid absolute URL, so this is purely defensive).
 */
export function buildEmbedUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('embed', '1');
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * The one SmartVault affordance for file pages, in two scopes:
 *
 * - WITH `accountId` (the account Files tab, /accountdetails/:id/files): shown
 *   when THIS account has a syncable SmartVault connection; opens the
 *   account-scoped folder view, titled with the account name.
 * - WITHOUT `accountId` (the firm-wide /files page): shown when the firm has
 *   ANY syncable SmartVault connection; opens the firm-wide Mirror view and
 *   offers firm admins a shortcut to the account-links settings page.
 *
 * Availability is decided entirely by the backend
 * (GET /api/v5/firms/smart_vault/availability, optionally account-scoped):
 * true when there's a sync-eligible mapping (smartvault_sync_eligible —
 * confirmed + primary owner, LISD-2541), 404 when the `enableSmartVaultMirror`
 * flag is off for the firm. The frontend does NOT re-evaluate the flag or
 * re-compose eligibility conditions — 404/false/error all mean "hidden".
 *
 * The folder view itself is liscio-frontend's, embedded in a modal iframe via
 * `?embed=1`. The iframe mounts (hidden, keepMounted) as soon as the button
 * renders: the embedded app's boot-to-roots is ~5s on staging, so it must warm
 * during page-dwell time — a hover-triggered mount was tried and the
 * hover-to-click gap is far too short to hide that, leaving users watching the
 * modal assemble (LISD-2545 review). The hidden-boot cost only applies to
 * pages where the button shows (firms/accounts with a syncable mapping).
 *
 * The embedded view authenticates via the shared `*.liscio.me` cookie. In a
 * third-party iframe context that can fail (Safari ITP, a CSP frame block, a
 * network error), leaving the modal blank — so we always offer an "Open in new
 * tab" escape hatch (a first-party load, which authenticates reliably) and
 * surface an explicit message if the frame errors outright.
 */
function SmartVaultButton({ accountId, accountName }) {
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const accountScoped = Boolean(accountId);
  const smartVaultUrl = useGenerateExternalUrl(
    accountScoped
      ? `/external/smart-vault/account/${accountId}`
      : '/external/smart-vault',
  );
  const storageData = getStorageData();
  const isFirmAdmin = storageData && storageData.role === FIRM_ADMIN;

  useEffect(() => {
    let active = true;
    // Clear stale availability whenever the scope changes — without this a
    // failed/negative probe for the next account would leave the previous
    // scope's button visible.
    setAvailable(false);
    Axios.get(SMART_VAULT_AVAILABILITY, {
      // account_id omitted for the firm-wide scope — any syncable mapping
      // qualifies.
      params: accountScoped ? { account_id: accountId } : {},
    })
      .then((res) => {
        if (active) setAvailable(res.data?.data?.available === true);
      })
      .catch(() => {
        // Availability probe only — 404 (Mirror flag off for the firm) and
        // errors alike simply mean "don't show the button".
      });
    return () => {
      active = false;
    };
  }, [accountScoped, accountId]);

  if (!available) return null;

  const embedUrl = buildEmbedUrl(smartVaultUrl);

  return (
    <Stack direction="row" alignItems="center">
      <Divider
        orientation="vertical"
        sx={{ height: 28, mx: 3.75, borderRightWidth: 2 }}
      />
      <Button
        type="button"
        variant="outlined"
        aria-label="SmartVault"
        onClick={() => setOpen(true)}
        data-testid="files__smartvault_btn"
        sx={{
          // Sized to sit just shy of the neighboring filter chips' height.
          height: 40,
          px: 1.25,
          borderWidth: 2,
          borderColor: SMARTVAULT_MULBERRY,
          color: SMARTVAULT_MULBERRY,
          '&:hover': {
            borderWidth: 2,
            borderColor: SMARTVAULT_MULBERRY,
            backgroundColor: 'rgba(123, 0, 95, 0.04)',
          },
        }}
      >
        <SmartVaultLogo style={{ height: 22, width: 'auto' }} aria-hidden />
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        // keepMounted + rendered from button-render (see docblock): the
        // embedded view warms during page-dwell so the click reveals an
        // already-loaded view, and reopen is instant.
        keepMounted
        maxWidth={false}
        // Right-align over the content area so the modal clears the ~200px
        // left main nav rather than overlapping it.
        sx={{
          '& .MuiDialog-container': { justifyContent: 'flex-end', pr: 5 },
        }}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 300px)',
            maxWidth: 1500,
            height: '90vh',
            m: 0,
            // Subtle SmartVault branding: mulberry accent strip on top.
            borderTop: `4px solid ${SMARTVAULT_MULBERRY}`,
          },
        }}
        aria-labelledby="smartvault-folder-view-title"
      >
        <DialogTitle
          id="smartvault-folder-view-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.625}>
            {/* Brand lockup IS the title; account scope appends the account
                  name. Firm-wide, the logo alone suffices. */}
            <SmartVaultLogo
              style={{ height: 22, width: 'auto' }}
              aria-label="SmartVault"
            />
            {accountScoped && accountName && (
              <Typography
                component="span"
                variant="h6"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                — {accountName}
              </Typography>
            )}
            {/* First-party escape hatch: opens the folder view in a new tab.
                  Always available, so a blocked/blank iframe is never a dead
                  end. Icon-only — the tooltip/aria-label carry the name. */}
            <Tooltip title="Open in new tab">
              <IconButton
                component="a"
                href={smartVaultUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open in new tab"
                size="small"
                color="primary"
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.625}>
            {/* Firm-admin-only shortcut to the SmartVault account-links
                  settings page — firm-wide scope only (the account modal stays
                  lean). In-app navigation: leaving the page unmounts this
                  dialog. */}
            {!accountScoped && isFirmAdmin && (
              <Button
                component={RouterLink}
                to="/profile_settings/smartvault_account_links"
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<SettingsOutlinedIcon />}
                data-testid="files__smartvault_settings_link"
                sx={{ fontWeight: 600 }}
              >
                Settings
              </Button>
            )}
            <IconButton
              aria-label="close"
              onClick={() => setOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, flex: 1, position: 'relative' }}>
          <iframe
            src={embedUrl}
            title="SmartVault folder view"
            // Clear the error overlay whenever the frame loads successfully
            // (the keepMounted frame can recover on a later/retried load);
            // onError surfaces it. We intentionally don't reset on open/close:
            // with keepMounted the frame doesn't reload on reopen, so clearing
            // the flag there would just unhide a still-blank frame after a
            // real failure.
            onLoad={() => setLoadError(false)}
            onError={() => setLoadError(true)}
            style={{ width: '100%', height: '100%', border: 0 }}
          />
          {loadError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.625,
                bgcolor: 'background.paper',
                p: 1.875,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1">
                The folder view couldn’t be loaded here.
              </Typography>
              <Link
                href={smartVaultUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3125,
                  fontWeight: 600,
                }}
              >
                Open SmartVault in a new tab
                <OpenInNewIcon fontSize="small" />
              </Link>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

export default SmartVaultButton;
